import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { Table } from "react-bootstrap";
import {
  CheckCircleFill,
  XCircleFill,
  Ban,
  Play,
  PlayCircle,
  InfoCircle,
  ChatRightText,
} from "react-bootstrap-icons";
import { Tooltip } from "react-tooltip";

import { CellActions } from "../../../common/cell-actions/cell-actions";
import { Loading } from "../../../common/loading/loading";

export const ScheduledJobTable = ({
  jobs,
  handleDisabled,
  handleRun,
  handleShowLog,
}) => {
  const intl = useIntl();

  const TableBody = ({ jobs }) => {
    if (jobs.length === 0) {
      return <NotFoundTableBody />;
    }
    return jobs.map((job) => (
      <tr key={job.id}>
        <td className="col-id">{job.id}</td>
        <td className="col-class-name">{job.className}</td>
        <JobIsActiveCell job={job} />
        <td className="col-last-running">{job.lastRunning}</td>
        <JobTimingCell job={job} />
        <JobStatusCell job={job} />
        <JobActionCell job={job} />
      </tr>
    ));
  };

  const NotFoundTableBody = () => (
    <tr>
      <td colSpan={6}>
        <span className="text-danger">
          <b>
            <FormattedMessage id="data.scheduledjobs.warning.notfoundjobs" />
          </b>
        </span>
      </td>
    </tr>
  );

  const JobIsActiveCell = ({ job }) => (
    <td className="col-active">
      <>
        {job.active ? (
          <FormattedMessage id="data.scheduledjobs.data.active.value.active" />
        ) : (
          <FormattedMessage id="data.scheduledjobs.data.active.value.inactive" />
        )}{" "}
        (
        {
          <FormattedMessage
            id={`data.scheduledjobs.data.active.currentstatus.${job.currentStatus}`}
          />
        }
        )
      </>
    </td>
  );

  const JobTimingCell = ({ job }) => (
    <td className="col-timing">
      {!job.active ? null : (
        <>
          {job.startTime} ({job.delayInSecond} sec)
        </>
      )}
    </td>
  );

  const JobStatusCell = ({ job }) => (
    <td className="col-status">
      {!job.active ? null : (
        <>
          {job.ok ? (
            <CheckCircleFill className="text-success" />
          ) : (
            <XCircleFill className="text-danger" />
          )}
          {job.msg ? (
            <>
              <span
                data-tooltip-id={`status-tooltip-${job.id}`}
                data-tooltip-content={job.msg}
                data-tooltip-place="bottom"
              >
                <InfoCircle className="ms-2" />
              </span>
              <Tooltip id={`status-tooltip-${job.id}`} />
            </>
          ) : null}
        </>
      )}
    </td>
  );

  const JobActionCell = ({ job }) => {
    const buttons = [];
    if (job.active) {
      buttons.push({
        variant: "secondary",
        title: intl.formatMessage({ id: "page.scheduledjobs.button.showLog" }),
        icon: <ChatRightText />,
        action: () => {
          handleShowLog(job.id);
        },
      });
      buttons.push({
        variant: "danger",
        title: intl.formatMessage({ id: "page.scheduledjobs.button.stopJob" }),
        icon: <Ban />,
        action: () => {
          handleDisabled(job.id, true);
        },
      });
      buttons.push({
        variant: "secondary",
        title: intl.formatMessage({ id: "page.scheduledjobs.button.runJob" }),
        icon: <Play />,
        action: () => {
          handleRun(job.id);
        },
      });
    } else {
      buttons.push({
        variant: "success",
        title: intl.formatMessage({ id: "page.scheduledjobs.button.startJob" }),
        icon: <PlayCircle />,
        action: () => {
          handleDisabled(job.id, false);
        },
      });
    }

    return (
      <td className="col-actions">
        <CellActions buttons={buttons} />
      </td>
    );
  };

  const LoadingBody = () => (
    <tr>
      <td colSpan={7}>
        <Loading />
      </td>
    </tr>
  );

  return (
    <>
      <div className="jobs-table-component table-responsive">
        <Table striped bordered>
          <thead>
            <tr>
              <th className="col-id">
                <FormattedMessage id="data.scheduledjobs.data.id.label" />
              </th>
              <th className="col-class-name">
                <FormattedMessage id="data.scheduledjobs.data.className.label" />
              </th>
              <th className="col-active">
                <FormattedMessage id="data.scheduledjobs.data.active.label" />
              </th>
              <th className="col-last-running">
                <FormattedMessage id="data.scheduledjobs.data.lastRunning.label" />
              </th>
              <th className="col-timing">
                <FormattedMessage id="data.scheduledjobs.data.timing.label" />
              </th>
              <th className="col-status">
                <FormattedMessage id="data.scheduledjobs.data.status.label" />
              </th>
              <th className="col-actions"></th>
            </tr>
          </thead>
          <tbody>{jobs ? <TableBody jobs={jobs} /> : <LoadingBody />}</tbody>
        </Table>
      </div>
    </>
  );
};

ScheduledJobTable.propTypes = {
  jobs: PropTypes.array,
  handleDisabled: PropTypes.func.isRequired,
  handleRun: PropTypes.func.isRequired,
  handleShowLog: PropTypes.func.isRequired,
};
